<template>
  <div>
    <button
      v-if="!visible && isHome"
      type="button"
      class="cs-launcher"
      :class="{ 'cs-launcher--offline': !connected && isLogin }"
      :aria-label="$t('customerService.title')"
      @click="open"
    >
      <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 5.5h16v10H9l-5 3.5V5.5z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
        <path d="M8 9h8M8 12.5h5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
      </svg>
      <span v-if="unreadCount > 0" class="cs-launcher-badge">{{ unreadLabel }}</span>
    </button>
    <transition name="cs-fade">
      <div v-if="visible" class="cs-root" :class="{ 'cs-root--mobile': !isPc, 'cs-root--pc': isPc }">
      <div class="cs-panel">
        <header class="cs-header">
          <span>
            <span class="cs-title">{{ $t('customerService.title') }}</span>
            <small class="cs-status" :class="{ 'cs-status--offline': !connected }">
              {{ connected ? $t('customerService.connected') : $t('customerService.reconnecting') }}
            </small>
          </span>
          <button type="button" class="cs-close" @click="close" :aria-label="$t('customerService.close')">×</button>
        </header>
        <div ref="messageList" class="cs-messages">
          <div v-if="loadingMore" class="cs-loading">{{ $t('customerService.loading') }}</div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="cs-msg-row"
            :class="msg.senderType === 1 ? 'cs-msg-row--mine' : 'cs-msg-row--other'"
          >
            <div class="cs-bubble">
              <button
                v-if="msg.msgType === 'image' && !failedImages[msg.id]"
                type="button"
                class="cs-image-link"
                :title="$t('customerService.openImage')"
                :aria-label="$t('customerService.openImage')"
                @click="openImagePreview(msg.content)"
              >
                <img
                  class="cs-image"
                  :src="imageUrl(msg.content)"
                  :alt="$t('customerService.imageAlt')"
                  @error="markImageFailed(msg.id)"
                />
              </button>
              <span
                v-else-if="msg.msgType === 'image'"
                class="cs-image-fallback"
                role="img"
                :aria-label="$t('customerService.imageUnavailable')"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6"/>
                  <path d="m6 16 3.2-3.2 2.6 2.6 2-2 4.2 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="m5 5 14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
                <span>{{ $t('customerService.imageUnavailable') }}</span>
              </span>
              <span v-else class="cs-text">{{ msg.content }}</span>
            </div>
            <div class="cs-time">{{ formatTime(msg.createdAt || msg.createTime) }}</div>
          </div>
        </div>
        <footer class="cs-footer">
          <label
            class="cs-upload-btn"
            :class="{ 'cs-upload-btn--disabled': sending || uploadingImage }"
            :aria-label="$t('customerService.sendImage')"
            :title="$t('customerService.sendImage')"
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.7"/>
              <circle cx="8.5" cy="9" r="1.5" stroke="currentColor" stroke-width="1.7"/>
              <path d="m5.5 17 4.2-4.2 3.2 3.2 2.2-2.2 3.4 3.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <input
              class="cs-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              :disabled="sending || uploadingImage"
              :aria-label="$t('customerService.sendImage')"
              @change="sendImage"
            />
          </label>
          <textarea
            v-model="draft"
            class="cs-input"
            rows="2"
            :placeholder="$t('customerService.placeholder')"
            @keydown.enter.exact.prevent="sendText"
          />
          <button type="button" class="cs-send" :disabled="sending || uploadingImage" @click="sendText">
            {{ uploadingImage ? '…' : $t('customerService.send') }}
          </button>
        </footer>
      </div>
      <div v-if="!isPc" class="cs-backdrop" @click="close" />
      <transition name="cs-fade">
        <div
          v-if="previewImageUrl"
          class="cs-image-preview-backdrop"
          @click.self="closeImagePreview"
        >
          <div
            class="cs-image-preview"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('customerService.imagePreview')"
          >
            <button
              type="button"
              class="cs-image-preview-close"
              :aria-label="$t('customerService.close')"
              @click="closeImagePreview"
            >×</button>
            <img
              :src="previewImageUrl"
              :alt="$t('customerService.imagePreview')"
              @error="closeImagePreview"
            />
          </div>
        </div>
      </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { csBus } from '@/utils/csBus'
import { getToken } from '@/utils/auth'
import { VUE_APP_BASEURL } from '@/config'
import { resolveSupportImageUrl } from '@/utils/supportImages'
import {
  getCsSession,
  getCsMessages,
  sendCsImage,
  sendCsMessage,
  markCsRead,
  heartbeatCsSession
} from '@/api/cs'

export default {
  name: 'CustomerChat',
  data() {
    return {
      visible: false,
      session: null,
      messages: [],
      failedImages: {},
      previewImageUrl: '',
      draft: '',
      sending: false,
      uploadingImage: false,
      loadingMore: false,
      pollTimer: null,
      retryDelay: 5000,
      unreadCount: 0,
      connected: true,
      notificationReady: false
    }
  },
  computed: {
    ...mapState(['isPc', 'isLogin', 'token']),
    isHome() {
      return this.$route && this.$route.name === 'home'
    },
    unreadLabel() {
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount)
    }
  },
  watch: {
    token() {
      this.resetBackgroundSync()
    },
    isLogin() {
      this.resetBackgroundSync()
    }
  },
  mounted() {
    csBus.$on('open', this.open)
    window.addEventListener('online', this.onNetworkOnline)
    window.addEventListener('offline', this.onNetworkOffline)
    window.addEventListener('keydown', this.onPreviewKeydown)
    this.resetBackgroundSync()
  },
  beforeDestroy() {
    csBus.$off('open', this.open)
    window.removeEventListener('online', this.onNetworkOnline)
    window.removeEventListener('offline', this.onNetworkOffline)
    window.removeEventListener('keydown', this.onPreviewKeydown)
    this.stopBackgroundSync()
  },
  methods: {
    async open() {
      if (!getToken()) {
        this.$message.warning(this.$t('customerService.loginRequired'))
        return
      }
      this.visible = true
      try {
        const res = await getCsSession()
        if (res.code !== 200 && res.code !== 0) {
          throw new Error(res.message || 'session error')
        }
        this.session = res.result
        this.unreadCount = Number(res.result.unreadCount || 0)
        await this.loadMessages()
        this.$nextTick(() => this.scrollBottom())
        if (this.session && this.session.id) {
          await markCsRead({ sessionId: this.session.id }).catch(() => {})
          this.unreadCount = 0
        }
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('customerService.error'))
        this.visible = false
      }
    },
    close() {
      this.closeImagePreview()
      this.visible = false
    },
    async loadMessages(beforeId) {
      if (!this.session) return
      this.loadingMore = !!beforeId
      try {
        const res = await getCsMessages({
          sessionId: this.session.id,
          beforeId,
          limit: 50
        })
        const list = (res.result || [])
        if (beforeId) {
          this.messages = [...list, ...this.messages]
        } else {
          this.messages = list
        }
      } finally {
        this.loadingMore = false
      }
    },
    resetBackgroundSync() {
      this.stopBackgroundSync()
      this.retryDelay = 5000
      this.notificationReady = false
      if (!getToken()) {
        this.session = null
        this.unreadCount = 0
        this.connected = true
        return
      }
      this.scheduleSync(0)
    },
    stopBackgroundSync() {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    },
    scheduleSync(delay) {
      this.stopBackgroundSync()
      this.pollTimer = setTimeout(() => this.syncSupportState(), delay)
    },
    async syncSupportState() {
      if (!getToken()) return
      try {
        const response = await getCsSession()
        if (response.code !== 200 && response.code !== 0) {
          throw new Error(response.message || 'session error')
        }
        this.session = response.result
        await heartbeatCsSession()
        const nextUnread = Number(response.result.unreadCount || 0)
        if (this.notificationReady && nextUnread > this.unreadCount) {
          this.playNotificationSound()
        }
        this.unreadCount = nextUnread
        if (this.visible) {
          await this.loadMessages()
          await markCsRead({ sessionId: this.session.id })
          this.unreadCount = 0
        }
        this.connected = true
        this.notificationReady = true
        this.retryDelay = 5000
        this.scheduleSync(5000)
      } catch (e) {
        this.connected = false
        this.retryDelay = Math.min(this.retryDelay * 2, 30000)
        this.scheduleSync(this.retryDelay)
      }
    },
    onNetworkOnline() {
      this.connected = true
      this.scheduleSync(0)
    },
    onNetworkOffline() {
      this.connected = false
    },
    playNotificationSound() {
      if (typeof window.AudioContext !== 'function') return
      const context = new window.AudioContext()
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      oscillator.frequency.value = 880
      gain.gain.setValueAtTime(0.12, context.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.28)
      oscillator.connect(gain)
      gain.connect(context.destination)
      oscillator.start()
      oscillator.stop(context.currentTime + 0.28)
      oscillator.addEventListener('ended', () => context.close(), { once: true })
    },
    async sendText() {
      const text = (this.draft || '').trim()
      if (!text || !this.session) return
      const sent = await this.sendPayload(text)
      if (sent) this.draft = ''
    },
    async sendImage(event) {
      const input = event.target
      const file = input.files && input.files[0]
      input.value = ''
      if (!file || !this.session || this.sending || this.uploadingImage) return
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.$message.error(this.$t('customerService.imageTypeUnsupported'))
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error(this.$t('customerService.imageTooLarge'))
        return
      }

      this.uploadingImage = true
      try {
        const form = new FormData()
        form.set('sessionId', this.session.id)
        form.set('file', file)
        const res = await sendCsImage(form)
        if (res.result) this.appendLocal(res.result)
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      } finally {
        this.uploadingImage = false
      }
    },
    async sendPayload(content) {
      if (!this.session || this.sending) return
      this.sending = true
      try {
        const res = await sendCsMessage({
          sessionId: this.session.id,
          msgType: 'text',
          content
        })
        if (res.result) {
          this.appendLocal(res.result)
        }
        return true
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
        return false
      } finally {
        this.sending = false
      }
    },
    appendLocal(msg) {
      if (!this.messages.find((m) => m.id === msg.id)) {
        this.messages.push(msg)
        this.$nextTick(() => this.scrollBottom())
      }
    },
    imageUrl(content) {
      return resolveSupportImageUrl(content, VUE_APP_BASEURL)
    },
    markImageFailed(messageId) {
      this.$set(this.failedImages, messageId, true)
    },
    openImagePreview(content) {
      this.previewImageUrl = this.imageUrl(content)
    },
    closeImagePreview() {
      this.previewImageUrl = ''
    },
    onPreviewKeydown(event) {
      if (event.key === 'Escape' && this.previewImageUrl) this.closeImagePreview()
    },
    scrollBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      const pad = (n) => (n < 10 ? '0' + n : '' + n)
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-launcher {
  position: fixed;
  z-index: 9999;
  right: 24px;
  bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  width: 54px;
  height: 54px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: var(--ld-btn-bg, #111);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.cs-launcher--offline::after {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 10px;
  height: 10px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #f59e0b;
  content: '';
}
.cs-launcher-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 21px;
  height: 21px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  border-radius: 999px;
  color: #fff;
  background: #ef4444;
  font-size: 11px;
  line-height: 1;
  box-sizing: border-box;
}
.cs-root {
  position: fixed;
  z-index: 10000;
}
.cs-root--pc {
  right: 24px;
  bottom: 24px;
  width: 380px;
  height: 520px;
}
.cs-root--mobile {
  inset: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.cs-backdrop {
  position: fixed;
  inset: 0;
  background: var(--ld-overlay, rgba(17, 17, 17, 0.36));
  z-index: -1;
}
.cs-panel {
  display: flex;
  flex-direction: column;
  background: var(--ld-bg-card, #fff);
  color: var(--ld-text, #111);
  border: 1px solid var(--ld-border, #ececec);
  box-shadow: var(--ld-shadow, 0 16px 48px rgba(0, 0, 0, 0.12));
  overflow: hidden;
}
.cs-root--pc .cs-panel {
  width: 100%;
  height: 100%;
  border-radius: 16px;
}
.cs-root--mobile .cs-panel {
  width: 100%;
  height: calc(100% - 12px);
  margin-top: 12px;
  border-radius: 16px 16px 0 0;
  border: none;
  border-top: 1px solid var(--ld-border, #ececec);
  position: relative;
  z-index: 1;
  overflow: hidden;
}
.cs-root--mobile .cs-header {
  padding: 20px 18px;
  min-height: 56px;
  box-sizing: border-box;
}
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--ld-bg-card, #fff);
  color: var(--ld-text, #111);
  border-bottom: 1px solid var(--ld-border, #ececec);
  flex-shrink: 0;
}
.cs-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--ld-text, #111);
}
.cs-status {
  margin-top: 3px;
  display: block;
  color: #16a34a;
  font-size: 10px;
  font-weight: 500;
}
.cs-status--offline {
  color: #d97706;
}
.cs-close {
  border: none;
  background: transparent;
  color: var(--ld-text-secondary, #888);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.15s ease, background 0.15s ease;
  border-radius: 8px;
  &:hover {
    color: var(--ld-text, #111);
    background: var(--ld-bg-active, #f2f2f2);
  }
}
.cs-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: var(--ld-bg, #fff);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.cs-loading {
  text-align: center;
  font-size: 12px;
  color: var(--ld-text-tertiary, #999);
  margin-bottom: 8px;
}
.cs-msg-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  max-width: 78%;
  width: fit-content;
}
.cs-msg-row--mine {
  align-self: flex-end;
  align-items: flex-end;
  margin-left: auto;
}
.cs-msg-row--other {
  align-self: flex-start;
  align-items: flex-start;
  margin-right: auto;
}
.cs-bubble {
  padding: 10px 12px;
  border-radius: 12px;
  word-break: break-word;
}
.cs-msg-row--mine .cs-bubble {
  background: var(--ld-btn-bg, #111);
  color: var(--ld-btn-text, #fff);
  border: 1px solid transparent;
}
.cs-msg-row--other .cs-bubble {
  background: var(--ld-bg-card, #fff);
  color: var(--ld-text, #111);
  border: 1px solid var(--ld-border, #ececec);
}
.cs-text {
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}
.cs-image-link,
.cs-image {
  display: block;
}
.cs-image-link {
  margin: -6px -8px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  cursor: zoom-in;
}
.cs-image {
  max-width: 220px;
  max-height: 240px;
  border-radius: 9px;
  object-fit: contain;
}
.cs-image-fallback {
  width: 150px;
  min-height: 76px;
  margin: -6px -8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px dashed var(--ld-border, #d7d7d7);
  border-radius: 9px;
  color: var(--ld-text-tertiary, #888);
  background: var(--ld-bg-muted, #f7f7f7);
  font-size: 12px;
  text-align: center;
}
.cs-image-preview-backdrop {
  position: fixed;
  z-index: 10002;
  inset: 0;
  padding: 24px;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.86);
  cursor: zoom-out;
  box-sizing: border-box;
}
.cs-image-preview {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  display: grid;
  place-items: center;
  cursor: default;
}
.cs-image-preview > img {
  width: auto;
  max-width: min(1100px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
  border-radius: 10px;
  object-fit: contain;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
}
.cs-image-preview-close {
  position: absolute;
  z-index: 1;
  top: 10px;
  right: 10px;
  width: 38px;
  height: 38px;
  padding: 0;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(17, 24, 39, 0.82);
  font-size: 25px;
  line-height: 1;
  cursor: pointer;
}
.cs-time {
  font-size: 11px;
  color: var(--ld-text-tertiary, #999);
  margin-top: 8px;
  line-height: 1.2;
}
.cs-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--ld-border, #ececec);
  background: var(--ld-bg-card, #fff);
  flex-shrink: 0;
}
.cs-upload-btn {
  width: 40px;
  height: 40px;
  border: 1px solid var(--ld-border, #ececec);
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ld-text-secondary, #888);
  background: var(--ld-bg-card, #fff);
  cursor: pointer;
  flex-shrink: 0;
}
.cs-upload-btn--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.cs-file {
  display: none;
}
.cs-input {
  flex: 1;
  border: 1px solid var(--ld-border, #ececec);
  border-radius: 10px;
  padding: 10px 12px;
  min-height: 40px;
  max-height: 88px;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  align-self: center;
  background: var(--ld-bg-card, #fff);
  color: var(--ld-text, #111);
  transition: border-color 0.15s ease, background 0.15s ease;
  &::placeholder {
    color: var(--ld-text-tertiary, #999);
  }
  &:focus {
    border-color: var(--ld-border-strong, #d0d0d0);
    background: var(--ld-bg-muted, #f7f7f7);
  }
}
.cs-send {
  height: 40px;
  padding: 0 16px;
  background: var(--ld-btn-bg, #111);
  color: var(--ld-btn-text, #fff);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;
  &:disabled {
    opacity: 0.45;
  }
}
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.2s ease;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
}
</style>
